import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { FloatingTooltip } from "@/components/ui/floating-tooltip";
import { forwardRef } from "react";

const sizeMap = {
  goal: "size-16 shadow-md hover:shadow-lg",
  routine: "size-16 shadow-xs hover:shadow-md",
  step: "size-12 shadow-2xs hover:shadow-xs",
};

const iconSizeMap = {
  goal: "size-8 min-w-[2rem] min-h-[2rem]",
  routine: "size-7 min-w-[1.75rem] min-h-[1.75rem]",
  step: "size-5 min-w-[1.25rem] min-h-[1.25rem]",
};

interface ProgressIconProps {
  type: "goal" | "routine" | "step";
  text: string;
  icon: LucideIcon;
  color: string;
  className?: string;
  disabledTooltip?: boolean;
  usePortal?: boolean;
}

// Update to Goals => Routines => Steps
export const ProgressIcon = forwardRef<HTMLDivElement, ProgressIconProps>(
  (
    { type, text, icon: Icon, color, className, usePortal = true, disabledTooltip = false },
    ref,
  ) => {
    return (
      <div className={cn("inline-block z-10", className)} ref={ref}>
        <FloatingTooltip
          placement="top"
          usePortal={usePortal}
          disabledTooltip={disabledTooltip}
          trigger={
            <div
              aria-label={text}
              className={cn(
                "rounded-full",
                "transition-all duration-200",
                "hover:scale-105",
                "flex items-center justify-center",
                sizeMap[type],
                color,
              )}
            >
              <Icon className={iconSizeMap[type]} />
            </div>
          }
        >
          <p className="text-center text-sm leading-relaxed text-popover-foreground">{text}</p>
        </FloatingTooltip>
      </div>
    );
  },
);

ProgressIcon.displayName = "ProgressIcon";

export default ProgressIcon;
